/**
 * The store and the opt-in rewarded ads on the game's own screens (port addition; the host's side:
 * src/game/monetization.ts FWPDeps.monetization). new_interf.render calls renderMonetization last for every screen;
 * the click, hover and texts of the controls are here. Without a store's catalogue or ads (a server without them)
 * nothing is added and the screens stay the original ones.
 *
 * PRODUCT RULE: an ad plays only after the player presses one of these opt-in buttons, each naming its exact bonus;
 * nothing here starts an ad by itself and every screen works without them. The buttons:
 *  - grave find (grave_result): "one more find" from the grave just searched (ad_grave_extra);
 *  - darts (roul): a free throw at the 3000 gold bet (ad_darts_free);
 *  - daily present (present): the present twice (ad_daily_gift_x2), on a phone only, beside the window's own "Accept"
 *    (renderPresent: shown even where there are no rewarded ads yet, whose skeleton says they are coming);
 *  - victory (end_game_win): the victory's gold once more (ad_battle_double; where the VK wall post button was);
 *  - fatigue (tired): -30 fatigue (ad_rest).
 * The store: the "Buy diamonds" block of the exchange screen (menu_pay) with its six original buttons (the VK payment
 * of the original): the packs of the catalogue (the server's store.catalog, nothing of it written here) with the
 * price in the player's currency, the amount, the bonus badge and the first purchase's "x2", a diamonds / gold switch
 * and the limited offer (the starter pack) under them; a tap opens the purchase dialog (ui/store-dialog.ts), hovering
 * a button shows what it gives. The "+" beside the diamonds of the main map and the "Buy diamonds" button of the shop,
 * the darts and Papa John's shop lead there. Where there is no payment yet (the web page, a build or a server without
 * one) the store is its skeleton: the same offers, and a purchase ends in "payment is not connected yet".
 *
 * The Russian text is the source and the translation key (src/game/runtime/i18n.ts; tools/i18n/extra.json).
 */
import { Bitmap, Event, MouseEvent, Sprite, TextField, TextFormat } from '@flash';
import type { AdAvailability, AdContext, AdPlacementId, MonetizationPort, StoreBuyResult, StoreOffer, StorePayment } from './monetization';
import { BigButton, LABEL_COLOR, TextLink, fitText, label, setRawText, shrinkToFit } from '../ui/controls';
import { BADGE_COLOR, StoreDialog, type StoreDialogModel, type StoreDialogOutcome, TAG_COLOR, sticker } from '../ui/store-dialog';
import { type Language, language, touchTextsOn, translate } from './i18n';
import { DONATE_CAPTION_HTML, STORE_CAPTION_HTML } from './product-texts';

// ------------------------------------------------------------------------------------------------ texts

export const MSG_AD_GRAVE = 'Реклама: ещё находка';
export const MSG_AD_DARTS = 'Реклама: бесплатный бросок';
/** The daily present window: its own button (takes the present, closes the window) and, on a phone, the ad's. */
export const MSG_PRESENT_ACCEPT = 'Принять';
export const MSG_AD_GIFT = 'Удвоить бонус с рекламой';
/** The skeleton of the rewarded ads (no ads here yet): what a tap on their button says. */
export const MSG_AD_SOON = 'Реклама ещё не подключена — скоро!';
/** Where the present's ad button would be when it cannot be offered today. */
export const MSG_AD_CAPPED = 'Реклама на сегодня закончилась — приходите завтра.';
export const MSG_AD_DECLINED = 'Реклама отключена в настройках рекламы.';
export const MSG_AD_BATTLE = 'Реклама: ×2 золота';
export const MSG_AD_REST = 'Реклама: −30 усталости';
export const MSG_AD_GRANTED = 'Бонус начислен.';
export const MSG_AD_DELAYED = 'Бонус будет начислен, как только сервер получит подтверждение просмотра.';
export const MSG_AD_FAILED = 'Реклама сейчас недоступна.';
export const MSG_STORE_DIAMONDS = 'Алмазы';
export const MSG_STORE_GOLD = 'Золото';
export const MSG_STORE_BUY_GOLD = 'Купить золото';
export const MSG_STORE_BUY_DIAMONDS = 'Купить алмазы';
export const MSG_STORE_FIRST_DOUBLE = 'Первая покупка алмазов — вдвое больше!';
export const MSG_STORE_STARTER = 'Стартовый набор';
export const MSG_STORE_STARTER_TEXT = 'Алмазы, золото и стимуляторы. Один раз на аккаунт.';
export const MSG_STORE_ONCE = 'Один раз на аккаунт.';
export const MSG_STORE_DOUBLE = 'Первая покупка: вдвое больше.';
export const MSG_STORE_POPULAR = 'Популярный выбор.';
export const MSG_STORE_BEST = 'Самый выгодный.';
export const MSG_STORE_CREDITED = 'Покупка зачислена.';
export const MSG_STORE_PENDING = 'Платёж ожидает подтверждения: покупка будет зачислена после него.';
export const MSG_STORE_FAILED = 'Покупка не удалась. Деньги не списаны или будут возвращены магазином.';
export const MSG_STORE_CONTENTS = 'Содержимое';
export const MSG_UNIT_DIAMONDS = 'алм.';
export const MSG_UNIT_GOLD = 'зол.';
export const MSG_STIMPACK = 'стимпакет';
export const MSG_AD_PRIVACY = 'Настройки рекламы';
export const MSG_PLAY_GAMES_LINK = 'Play Игры';
export const MSG_PLAY_GAMES_LINKED = 'Аккаунт привязан к Google Play Играм.';
export const MSG_PLAY_GAMES_FAILED = 'Не удалось привязать Google Play Игры.';
export const MSG_PLAY_GAMES_SIGN_IN = 'Войти через Google Play Игры';
export const MSG_PLAY_GAMES_NOT_LINKED = 'Этот игрок Google Play Игр ещё не привязан к аккаунту игры: войдите по нику, затем привяжите его в настройках.';
export const MSG_PLAY_GAMES_SIGN_IN_FAILED = 'Не удалось войти через Google Play Игры.';
/** The victory popup's way on without searching the graves, where its ad button is not offered. */
export const MSG_CONTINUE = 'Дальше';
/** The exchange screen without a store's catalogue: where the diamonds come from, in the buy block's place. */
export const MSG_PAY_NO_STORE = 'Алмазы дают за новые уровни, достижения и задания, иногда — в подарок. Здесь их можно обменять на золото.';
/** The purchase dialog (ui/store-dialog.ts): its buttons, the words of a pack and the answers of the store. */
export const MSG_STORE_BUY = 'Купить';
export const MSG_STORE_CANCEL = 'отмена';
export const MSG_STORE_OK = 'Понятно';
export const MSG_STORE_WAIT = 'Подождите…';
export const MSG_STORE_BONUS_LINE = 'Бонус: +{n} % к базовому курсу.';
export const MSG_STORE_BASE_LINE = 'Базовый пакет, без бонуса.';
export const MSG_STORE_FIRST_LINE = 'Первая покупка алмазов ×{k}: {n} вместо {m}.';
export const MSG_STORE_TEST_NOTE = 'Сборка разработчика: тестовая покупка без оплаты.';
/** The skeleton of the shop (no payment here yet): what a purchase ends in. */
export const MSG_STORE_SOON = 'Оплата ещё не подключена — скоро!';
export const MSG_STORE_SOON_NOTE = 'Ничего не списано и ничего не начислено.';
/** The stickers of the buy buttons and of the starter pack. */
export const MSG_STORE_POPULAR_TAG = 'Популярный';
export const MSG_STORE_BEST_TAG = 'Самый выгодный';
export const MSG_STORE_ONCE_TAG = 'Один раз';
/** The hover text of the "donate" buttons where the store has offers. */
export const MSG_STORE_DONATE_HINT = 'Покупка алмазов и золота. Здесь же — обмен алмазов на золото: 1 алмаз = 1100 золота.';

/** Every literal of this module (the i18n completeness test reads them from here too). */
export const MONETIZATION_TEXTS = [
  MSG_AD_GRAVE, MSG_AD_DARTS, MSG_AD_GIFT, MSG_AD_BATTLE, MSG_AD_REST, MSG_AD_GRANTED, MSG_AD_DELAYED, MSG_AD_FAILED,
  MSG_PRESENT_ACCEPT, MSG_AD_SOON, MSG_AD_CAPPED, MSG_AD_DECLINED,
  MSG_STORE_DIAMONDS, MSG_STORE_GOLD, MSG_STORE_BUY_GOLD, MSG_STORE_BUY_DIAMONDS, MSG_STORE_FIRST_DOUBLE, MSG_STORE_STARTER,
  MSG_STORE_STARTER_TEXT, MSG_STORE_ONCE, MSG_STORE_DOUBLE, MSG_STORE_POPULAR, MSG_STORE_BEST, MSG_STORE_CREDITED,
  MSG_STORE_PENDING, MSG_STORE_FAILED, MSG_STORE_CONTENTS, MSG_UNIT_DIAMONDS, MSG_UNIT_GOLD, MSG_STIMPACK, MSG_AD_PRIVACY,
  MSG_PLAY_GAMES_LINK, MSG_PLAY_GAMES_LINKED, MSG_PLAY_GAMES_FAILED, MSG_PLAY_GAMES_SIGN_IN, MSG_PLAY_GAMES_NOT_LINKED,
  MSG_PLAY_GAMES_SIGN_IN_FAILED, MSG_PAY_NO_STORE, MSG_CONTINUE, MSG_STORE_BUY, MSG_STORE_CANCEL, MSG_STORE_OK, MSG_STORE_WAIT,
  MSG_STORE_BONUS_LINE, MSG_STORE_BASE_LINE, MSG_STORE_FIRST_LINE, MSG_STORE_TEST_NOTE, MSG_STORE_SOON, MSG_STORE_SOON_NOTE,
  MSG_STORE_POPULAR_TAG, MSG_STORE_BEST_TAG, MSG_STORE_ONCE_TAG, MSG_STORE_DONATE_HINT,
];

// ------------------------------------------------------------------------------------------------ helpers

const STATE = '$monetization';

interface ScreenState {
  refreshed: boolean;
  controls: Record<string, any>;
}

function portOf(fwp: any): MonetizationPort | null {
  return (fwp && fwp.deps && fwp.deps.monetization) || null;
}

function stateOf(screen: any): ScreenState {
  if (!screen[STATE]) screen[STATE] = { refreshed: false, controls: {} } as ScreenState;
  return screen[STATE];
}

/** Renders the screen again when it is still the current one (an async refresh came back). */
function rerender(interf: any, screen: any): void {
  const cur = interf.cur_screen && interf.cur_screen[interf.cur_screen[0]];
  if (cur && cur.link === screen && interf.FWP.main_mode === 'IDLE') interf.render();
}

/** Thousands with a thin separator, as the game prints gold ("61 600"). */
export function groupDigits(n: number): string {
  return String(Math.floor(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// ------------------------------------------------------------------------------------------------ ad buttons

interface AdSpec {
  screen: string;
  placement: AdPlacementId;
  x: number;
  y: number;
  /** Placed by its screen's own layout (renderPresent), not by placeAdButton. */
  own?: boolean;
  /** The button's label (source text; a number may follow on its own line); `max`: the placement's largest reward. */
  label(fwp: any, max: number | null): string;
  /** Whether the bonus applies on this screen now; null = no. */
  context(fwp: any): AdContext | null;
  /** The key of the bonus (once per battle, grave or day), or null when it may repeat. */
  key(fwp: any): string | null;
  /** The reward arrived (the game state is reloaded right after). */
  granted(interf: any, result: Record<string, any>): { after: string; notice: boolean };
}

const BATTLE_DOUBLE_MODES = ['career', 'missions', 'survival'];

export const AD_SPECS: readonly AdSpec[] = [
  {
    screen: 'grave_result',
    placement: 'ad_grave_extra',
    x: 330,
    y: 352,
    label: () => MSG_AD_GRAVE,
    context: (fwp) => (fwp.last_win && fwp.last_grave_index > 0 ? { battleId: fwp.last_win.battleId, index: fwp.last_grave_index } : null),
    key: (fwp) => (fwp.last_win ? `grave:${fwp.last_win.battleId}:${fwp.last_grave_index}` : null),
    granted(interf, r) {
      interf.FWP.grave_found = String(r.id ?? '');
      interf.FWP.grave_found_type = String(r.ptype ?? '');
      return { after: 'render', notice: false };
    },
  },
  {
    screen: 'roul',
    placement: 'ad_darts_free',
    x: 640,
    y: 291,
    label: () => MSG_AD_DARTS,
    context: () => ({}),
    key: () => null,
    granted(interf, r) {
      interf.FWP.get_roul = r.prizeClass;
      interf.FWP.roul_weapon = r.weapon ?? '';
      return { after: 'init roul result', notice: false };
    },
  },
  {
    screen: 'present',
    placement: 'ad_daily_gift_x2',
    // (renderPresent places it beside the window's "Accept")
    x: 492,
    y: 371,
    own: true,
    label: () => MSG_AD_GIFT,
    context: (fwp) => (fwp.get_present ? {} : null),
    key: () => 'present',
    granted(interf) {
      interf.FWP.get_present = 0;
      return { after: 'remove', notice: true };
    },
  },
  {
    screen: 'end_game_win',
    placement: 'ad_battle_double',
    x: 253,
    y: 365,
    label: (fwp, max) => `${MSG_AD_BATTLE}\n+${groupDigits(Math.min(fwp.last_win ? fwp.last_win.gold : 0, max ?? Number.POSITIVE_INFINITY))}`,
    context: (fwp) => (fwp.last_win && fwp.last_win.gold > 0 && BATTLE_DOUBLE_MODES.indexOf(fwp.last_win.mode) != -1 ? { battleId: fwp.last_win.battleId } : null),
    key: (fwp) => (fwp.last_win ? `battle:${fwp.last_win.battleId}` : null),
    granted: () => ({ after: 'render', notice: true }),
  },
  {
    screen: 'tired',
    placement: 'ad_rest',
    // Under the fatigue value, above the long help text.
    x: 331,
    y: 165,
    label: () => MSG_AD_REST,
    context: (fwp) => (Number(fwp.player && fwp.player.tired) >= 50 ? {} : null),
    key: () => null,
    granted: () => ({ after: 'render', notice: true }),
  },
];

function placeAdButton(interf: any, screen: any, st: ScreenState, spec: AdSpec, m: MonetizationPort): void {
  const fwp = interf.FWP;
  const id = `ad:${spec.placement}`;
  let b = st.controls[id] as BigButton | undefined;
  const ctx = spec.context(fwp);
  const key = spec.key(fwp);
  const show = !!m.ads && ctx !== null && !(key && fwp.ad_used && fwp.ad_used[key]) && m.ads.offered(spec.placement);
  if (!show) {
    if (b) b.visible = false;
    return;
  }
  if (!b) {
    b = new BigButton(fwp, `ad_${spec.placement}`, spec.label(fwp, m.ads!.maxAmount(spec.placement)), 'beige');
    b.x = spec.x;
    b.y = spec.y;
    b.addEventListener(MouseEvent.CLICK, () => watchAd(interf, spec));
    screen.addChild(b);
    st.controls[id] = b;
  }
  b.setText(spec.label(fwp, m.ads!.maxAmount(spec.placement)));
  b.visible = true;
}

/** The player chose the ad of `spec`: the host shows it, the server grants the bonus, the game reloads its state. */
function watchAd(interf: any, spec: AdSpec): void {
  const fwp = interf.FWP;
  const c = spec.context(fwp);
  if (c === null) return;
  const k = spec.key(fwp);
  fwp.net_ad(spec.placement, c, (result: Record<string, any>) => {
    if (k) fwp.ad_used[k] = true;
    const g = spec.granted(interf, result);
    fwp.net_reload_state(g.after, () => {
      if (g.notice) fwp.net_notice(MSG_AD_GRANTED, 3000);
    });
  });
}

// ------------------------------------------------------------------------------------------------ the daily present

/**
 * The present window's buttons on a phone: 1.2 times the original's 145x43 (48 CSS px and more on a phone), their
 * taps taken a little beyond their art (PRESENT_TOUCH_HIT, the button's own coordinates: 48 CSS px and more on the
 * smallest phone too, where the window is shown smaller), not up to the other button.
 */
export const PRESENT_TOUCH_SCALE = 1.2;
const PRESENT_TOUCH_HIT = { x: -4, y: -8, width: 153, height: 59 };
/** Their row: under the "You got" line, about the middle between it and the window's bottom edge. */
const PRESENT_ROW_Y = 366;
const PRESENT_GAP = 16;
/** The line in the ad button's place (today's ads used up, the consent refused): up to three lines as wide as it. */
const PRESENT_WHY_LINES = 3;

/** Whether the present window offers its ad button here: a phone (touch mode: a phone, a tablet, the app). */
function mobileDevice(): boolean {
  return touchTextsOn();
}

/**
 * The daily present window (present): its own active button, "Accept", takes the present and closes the window (the
 * game's close arrow: present.claim); on a phone the opt-in "Double the bonus with an ad" beside it (ad_daily_gift_x2:
 * the server credits the present twice, the ad's verification first). Where there are no rewarded ads yet (the
 * skeleton: 'soon') it is shown all the same and a tap says they are coming, nothing credited; today's ads used up or
 * the ad consent refused: a line says so in its place; no ad to show yet: no button (it comes when one is loaded). A
 * desktop has "Accept" only. The pair is centred under the "You got" line; "Accept" alone in the middle.
 */
export function renderPresent(interf: any, screen: any, m: MonetizationPort | null): void {
  const fwp = interf.FWP;
  const st = stateOf(screen);
  const spec = AD_SPECS.find((a) => a.screen === 'present')!;
  const mobile = mobileDevice();
  const k = mobile ? PRESENT_TOUCH_SCALE : 1;
  let accept = st.controls.presentAccept as BigButton | undefined;
  if (!accept) {
    accept = new BigButton(fwp, 'present_accept', MSG_PRESENT_ACCEPT, 'green');
    // (a control of the port: the game's click of the close arrow, which claims the present and closes the window; its
    // waiting splash takes the taps until then)
    accept.addEventListener(MouseEvent.CLICK, () => {
      if (fwp.ad_busy) return;
      const arrow = screen.getChildByName('close_present') ?? { name: 'close_present' };
      interf.click({ target: arrow, currentTarget: arrow });
    });
    screen.addChild(accept);
    st.controls.presentAccept = accept;
  }
  // The ad's place: null = none (a desktop, nothing to double, doubled already).
  const due = spec.context(fwp) !== null && !(fwp.ad_used && fwp.ad_used[spec.key(fwp) as string]);
  const state: AdAvailability | null = !mobile || !due ? null : m && m.ads ? m.ads.state(spec.placement) : 'soon';
  let ad = st.controls.presentAd as BigButton | undefined;
  if (!ad && (state === 'ready' || state === 'soon')) {
    ad = new BigButton(fwp, `ad_${spec.placement}`, MSG_AD_GIFT, 'beige');
    ad.addEventListener(MouseEvent.CLICK, () => {
      if (fwp.ad_busy) return;
      const port = portOf(fwp);
      const now: AdAvailability = port && port.ads ? port.ads.state(spec.placement) : 'soon';
      // The skeleton: nobody is asked, nothing is credited; the window stays (its "Accept" takes the present).
      if (now === 'soon') fwp.net_notice(MSG_AD_SOON, 4000);
      else if (now === 'ready') watchAd(interf, spec);
      else interf.render();
    });
    screen.addChild(ad);
    st.controls.presentAd = ad;
  }
  let why = st.controls.presentWhy as TextField | undefined;
  const reason = state === 'capped' ? MSG_AD_CAPPED : state === 'declined' ? MSG_AD_DECLINED : '';
  if (!why && reason) {
    why = label('', { size: 12, bold: true, color: LABEL_COLOR, align: 'center', width: 145, lines: PRESENT_WHY_LINES });
    why.name = 'present_ad_reason';
    screen.addChild(why);
    st.controls.presentWhy = why;
  }
  const showAd = state === 'ready' || state === 'soon';
  if (ad) ad.visible = showAd;
  if (why) {
    why.visible = !!reason;
    if (reason && (why as any).$caption !== reason) {
      why.text = reason;
      (why as any).$caption = reason;
      // The whole reason in every language: a longer translation in a smaller font, the field as tall as its lines
      // (never a line cut off at its bottom; it is centred on the row by its own height below).
      fitText(why, PRESENT_WHY_LINES, 10);
      why.height = Math.ceil(why.textHeight) + 4;
    }
  }
  // The row, centred under the "You got" line (the window's middle); the two arts (43 and 40 px tall) on one middle.
  const line = screen.main;
  const mid = line ? Math.round(line.x + line.width / 2) : 404;
  const w = 145 * k;
  const pair = showAd || !!reason;
  const rowY = (b: BigButton) => {
    // (the art's own height, before a hit area of its own changed the button's bounds)
    if ((b as any).$artH === undefined) (b as any).$artH = b.button.getBounds(b).height;
    return Math.round(PRESENT_ROW_Y + ((43 - (b as any).$artH) * k) / 2);
  };
  accept.y = rowY(accept);
  if (ad) ad.y = rowY(ad);
  for (const b of [accept, ad]) {
    if (!b) continue;
    b.scaleX = b.scaleY = k;
    if ((b as any).$touchHit !== mobile) {
      (b as any).$touchHit = mobile;
      b.setHitRect(mobile ? PRESENT_TOUCH_HIT : null);
    }
  }
  accept.x = Math.round(pair ? mid - PRESENT_GAP / 2 - w : mid - w / 2);
  if (ad) ad.x = Math.round(mid + PRESENT_GAP / 2);
  if (why) {
    why.scaleX = why.scaleY = k;
    why.x = Math.round(mid + PRESENT_GAP / 2);
    why.y = Math.round(PRESENT_ROW_Y + (43 * k - why.height) / 2);
  }
}

// ------------------------------------------------------------------------------------------------ the store

/** The six original buy buttons of menu_pay: slot 1..3 the left column, 4..6 the right one. */
export const STORE_BUTTONS = ['donate1', 'donate3', 'donate10', 'donate25', 'donate50', 'donate100'] as const;

/** A child of `screen` placed at (x, y) (the "votes" / "diam_from_votes" fields repeat their names per row). */
function childNear(screen: any, x: number, y: number, pred: (c: any) => boolean): any {
  let best: any = null;
  let bestD = 8;
  for (let i = 0; i < screen.numChildren; i++) {
    const c = screen.getChildAt(i);
    if (!pred(c)) continue;
    const d = Math.abs(c.x - x) + Math.abs(c.y - y);
    if (d < bestD) {
      best = c;
      bestD = d;
    }
  }
  return best;
}

/** The gold coin of the exchange (menu_pay's own coin sprite, detached from a fresh instance of the screen). */
function coinSprite(fwp: any): any {
  const s = fwp.container_interf.get_object('menu_pay');
  const c = s ? childNear(s, 463.25, 425.85, (x) => !(typeof x.text === 'string') && !(x instanceof Bitmap)) : null;
  if (!c) return null;
  s.removeChild(c);
  c.scaleX = c.scaleY = 1;
  return c;
}

/** The diamond of the first buy button of menu_pay (detached from a fresh instance of the screen). */
function diamondSprite(fwp: any): any {
  const s = fwp.container_interf.get_object('menu_pay');
  const c = s ? childNear(s, 193.6 + 113.6, 172.9 + 4.6, (x) => /^instance\d+$/.test(String(x.name)) && !(x instanceof Bitmap) && typeof x.text !== 'string') : null;
  if (!c) return null;
  s.removeChild(c);
  c.scaleX = c.scaleY = 1;
  return c;
}

/** The offers of the pay screen's current tab by slot, and the offer of the offer button. */
function storeLayout(fwp: any, offers: StoreOffer[]): { slots: (StoreOffer | null)[]; offer: StoreOffer | null; tab: 'diamonds' | 'gold'; double: boolean } {
  const tab: 'diamonds' | 'gold' = fwp.store_tab === 'gold' ? 'gold' : 'diamonds';
  const slots: (StoreOffer | null)[] = [null, null, null, null, null, null];
  for (const o of offers) if (o.shop && o.shop.tab === tab && o.shop.slot >= 1 && o.shop.slot <= 6 && !slots[o.shop.slot - 1]) slots[o.shop.slot - 1] = o;
  const offer = offers.find((o) => o.shop && o.shop.tab === 'offer') ?? null;
  const double = tab === 'diamonds' && slots.some((o) => o && o.multiplier > 1);
  return { slots, offer, tab, double };
}

/**
 * The victory popup (end_game_win) where its ad button ("×2 gold") is not offered (the web page, a battle without
 * gold, the bonus taken): "Continue" in its place, beside "Search the graves" (the pair symmetric in the popup, not one
 * button off its middle), doing what the popup's close arrow does (close_win_game: on without the graves' search).
 */
export function renderWinContinue(interf: any, screen: any): void {
  const st = stateOf(screen);
  const spec = AD_SPECS.find((a) => a.screen === 'end_game_win')!;
  const ad = st.controls[`ad:${spec.placement}`];
  const offered = !!(ad && ad.visible);
  let b = st.controls.winContinue as BigButton | undefined;
  if (!b && !offered) {
    b = new BigButton(interf.FWP, 'close_win_game', MSG_CONTINUE, 'beige');
    b.x = spec.x;
    b.y = spec.y;
    // (a control of the port: the game's click with the close arrow's name)
    b.addEventListener(MouseEvent.CLICK, () => interf.click({ target: b, currentTarget: b }));
    screen.addChild(b);
    st.controls.winContinue = b;
  }
  if (b) b.visible = !offered;
}

/**
 * A list price in the player's currency (the owner's decision: UAH in Ukrainian, USD otherwise), written as that
 * language writes money: "39,99 ₴", "$0.99", "0,99 $". Another currency of the list only when neither is in it.
 */
export function formatListPrice(prices: Record<string, number>, lang: Language = language()): string {
  const codes = Object.keys(prices);
  const code = lang === 'uk' && prices.UAH != null ? 'UAH' : prices.USD != null ? 'USD' : codes[0];
  if (!code) return '';
  const [int, frac] = Number(prices[code]).toFixed(2).split('.');
  const whole = String(Math.floor(Number(int))).replace(/\B(?=(\d{3})+(?!\d))/g, lang === 'en' ? ',' : ' ');
  if (code === 'USD') return lang === 'en' ? `$${whole}.${frac}` : `${whole},${frac} $`;
  if (code === 'UAH') return lang === 'en' ? `UAH ${whole}.${frac}` : `${whole},${frac} ₴`;
  return lang === 'en' ? `${code} ${whole}.${frac}` : `${whole},${frac} ${code}`;
}

/** The price a pack shows: the platform store's own localized one, else the catalogue's list price. */
export function offerPrice(o: StoreOffer, lang: Language = language()): string {
  return o.price ?? formatListPrice(o.listPrices, lang);
}

/** The badge of a pack: its bonus over the base rate ("+20 %"); a bundle's value is told in its dialog instead. */
export function offerBadge(o: StoreOffer): string | null {
  return o.kind === 'currency_pack' && o.bonusPct > 0 ? `+${o.bonusPct} %` : null;
}

/** Whether this game has a store with offers (the catalogue arrived): the "+" beside the diamonds, the buy block. */
export function storeOffered(fwp: any): boolean {
  const m = portOf(fwp);
  const offers = m && m.store ? m.store.offers() : null;
  return !!(offers && offers.length > 0);
}

/**
 * The receipt paper under the buy block (shape 719: its fill 8, a whiter torn patch where the original's VK payment
 * block lay, 198-597 x 237-316) and the paper's own colour (fill 6).
 */
const PAY_PATCH = { x: 196, y: 235, width: 403, height: 83 };
const PAY_PAPER = 0xffffdb;
/** Where the note of the exchange screen without a store goes: the buy block's band, under the receipt's number. */
const PAY_NOTE = { x: 214, y: 196, width: 380 };

/**
 * The exchange screen (menu_pay) without a store's offers (no catalogue: a server without the store; the caller hid
 * the buy block): the whiter patch the block leaves on the paper is covered with the paper's colour and the band says
 * where diamonds come from, so the receipt is not half blank with a torn rectangle in it; the exchange below stays.
 */
export function renderPayWithoutStore(interf: any, screen: any, on = true): void {
  const st = stateOf(screen);
  // (the store's offers came meanwhile: its block again, the note and the cover gone)
  for (const k of ['payCover', 'payNote']) if (st.controls[k]) st.controls[k].visible = on;
  if (st.controls.noStore || !on) return;
  const cover = new Sprite();
  cover.name = 'pay_cover';
  cover.mouseEnabled = false;
  cover.graphics.beginFill(PAY_PAPER, 1);
  cover.graphics.drawRect(PAY_PATCH.x, PAY_PATCH.y, PAY_PATCH.width, PAY_PATCH.height);
  cover.graphics.endFill();
  // Right above the paper (the first child: shape 719), under everything drawn on it.
  screen.addChildAt(cover, Math.min(1, screen.numChildren));
  const note = label(MSG_PAY_NO_STORE, { size: 13, align: 'center', width: PAY_NOTE.width, lines: 4, color: 0x3b2508 });
  note.name = 'pay_no_store';
  note.x = PAY_NOTE.x;
  note.y = PAY_NOTE.y;
  screen.addChild(note);
  st.controls.payCover = cover;
  st.controls.payNote = note;
  st.controls.noStore = true;
  void interf;
}

/** The buy buttons' rows and columns are 50 and 210 px apart (menu_pay): a finger's target is the whole cell. */
const PAY_CELL = { width: 210, height: 50 };
/** The price field of a buy button: the part of the button left of its arrow (the original's field fits "25" only). */
const PAY_PRICE = { dx: 3, width: 86, size: 18 };
const PAY_AMOUNT = { dx: 138, width: 64, size: 18 };
const X2_COLOR = 0xe07000;

/** A transparent rectangle (alpha 0) that takes the taps of the SWF button it is the hit area of. */
function hitArea(x: number, y: number, width: number, height: number): Sprite {
  const s = new Sprite();
  s.name = '$hit';
  s.graphics.beginFill(0, 0);
  s.graphics.drawRect(x, y, width, height);
  s.graphics.endFill();
  return s;
}

function setFontSize(tf: any, size: number): void {
  const f = new TextFormat();
  f.size = size;
  tf.setTextFormat(f);
}

/** The field's font size as its text shows it. */
function fontSize(tf: any): number {
  return Number(tf.getTextFormat().size ?? tf.defaultTextFormat.size ?? 12);
}

/** A sticker of a buy button (kept per key; a new text draws it again), placed by its left or right edge. */
function placeSticker(screen: any, st: ScreenState, key: string, text: string | null, color: number, x: number, y: number, from: 'left' | 'right'): void {
  let s = st.controls[key];
  if (s && (s.$text !== text || !text)) {
    s.visible = false;
    if (!text) return;
    screen.removeChild(s);
    s = null;
  }
  if (!text) return;
  if (!s) {
    s = sticker(text, color);
    s.name = key;
    s.$text = text;
    screen.addChild(s);
    st.controls[key] = s;
  }
  s.x = Math.round(from === 'right' ? x - s.width : x);
  s.y = Math.round(y);
  s.visible = true;
}

/** The fields of buy button `i` (found once by the asset's own positions: they are moved and widened below). */
function payFields(screen: any, st: ScreenState, i: number, button: any): { price: any; amount: any; diamond: any } {
  const key = `fields${i}`;
  if (!st.controls[key]) {
    const price = childNear(screen, button.x + 29.6, button.y + 10.55, (c) => c.name === 'votes');
    const amount = childNear(screen, button.x + 148.6, button.y + 10.55, (c) => c.name === 'diam_from_votes');
    const diamond = childNear(screen, button.x + 113.6, button.y + 4.6, (c) => /^instance\d+$/.test(String(c.name)) && !(c instanceof Bitmap));
    for (const [f, spec] of [[price, PAY_PRICE], [amount, PAY_AMOUNT]] as const) {
      if (!f) continue;
      f.$y = f.y;
      // (the asset's embedded font is a subset without the currency signs: the game's Arial, bold, as the popups)
      const fmt = f.defaultTextFormat;
      fmt.font = 'Arial';
      fmt.bold = true;
      f.defaultTextFormat = fmt;
      f.x = button.x + spec.dx;
      f.width = spec.width;
      f.wordWrap = false;
      f.multiline = false;
    }
    // The texts and the icon over the button never take its taps.
    for (const f of [price, amount, diamond]) if (f) f.mouseEnabled = false;
    if (diamond) diamond.mouseChildren = false;
    // A finger's target: the whole cell of the button (the gaps between the buttons belong to them).
    const b = button.getBounds(button);
    button.hitTestState = hitArea(b.x - (PAY_CELL.width - b.width) / 2, b.y - (PAY_CELL.height - b.height) / 2, PAY_CELL.width, PAY_CELL.height);
    st.controls[key] = { price, amount, diamond };
  }
  return st.controls[key];
}

/** One font size for a set of fields (the largest at which every text fits), their lines kept where they were. */
function evenFont(fields: any[], size: number, min: number): void {
  let k = size;
  for (const f of fields) {
    setFontSize(f, size);
    shrinkToFit(f, min);
    k = Math.min(k, fontSize(f));
  }
  for (const f of fields) {
    setFontSize(f, k);
    if (typeof f.$y === 'number') f.y = f.$y + Math.round((size - k) * 0.55);
  }
}

/**
 * The "Buy" block of menu_pay (the exchange screen) with the store's offers: the six original buttons show the
 * packs of the tab (diamonds / gold) with the price in the player's currency, the amount, the bonus badge ("+20 %"),
 * the tag of the popular and the best-value pack and the first purchase's "×2"; above them the tabs and the
 * first-purchase note, under them the starter pack while it is offered. A tap opens the purchase dialog. Returns
 * false without offers (the caller then hides the block as before).
 */
export function renderStorePay(interf: any, screen: any): boolean {
  const fwp = interf.FWP;
  const m = portOf(fwp);
  const offers = m && m.store ? m.store.offers() : null;
  if (!offers || offers.length === 0) return false;
  const st = stateOf(screen);
  const { slots, offer, tab, double } = storeLayout(fwp, offers);
  const lang = language();
  const prices: any[] = [];
  const amounts: any[] = [];
  STORE_BUTTONS.forEach((name, i) => {
    const button = screen.getChildByName(name);
    if (!button) return;
    const o = slots[i];
    const { price, amount, diamond } = payFields(screen, st, i, button);
    button.visible = o !== null;
    for (const f of [price, amount]) if (f) f.visible = o !== null;
    if (diamond) diamond.visible = o !== null && tab === 'diamonds';
    const b = button.getBounds(screen);
    // An empty cell (the gold tab has four packs): the paper over its arrow and the whiter patch of the old block, down
    // to the next cell of its column when that one is empty too (the block's whiter strip between two blanks showed as
    // a light line across the empty column).
    let blank = st.controls[`blank${i}`];
    if (!blank && !o) {
      blank = new Sprite();
      blank.name = `store_blank${i}`;
      blank.mouseEnabled = false;
      screen.addChild(blank);
      st.controls[`blank${i}`] = blank;
    }
    if (blank) {
      blank.visible = !o;
      const below = i % 3 < 2 ? screen.getChildByName(STORE_BUTTONS[i + 1]) : null;
      const bottom = below && !slots[i + 1] ? below.getBounds(screen).y - 3 : b.y + b.height + 3;
      blank.graphics.clear();
      blank.graphics.beginFill(PAY_PAPER, 1);
      blank.graphics.drawRect(b.x - 3, b.y - 3, b.width + 6, Math.max(b.height + 6, bottom - (b.y - 3)));
      blank.graphics.endFill();
    }
    placeSticker(screen, st, `store_badge${i}`, o ? offerBadge(o) : null, BADGE_COLOR, b.x + b.width + 4, b.y - 9, 'right');
    placeSticker(screen, st, `store_tag${i}`, o && o.label ? translate(o.label === 'popular' ? MSG_STORE_POPULAR_TAG : MSG_STORE_BEST_TAG) : null, TAG_COLOR, b.x - 4, b.y - 9, 'left');
    // The first purchase's "×2" on the diamond's lower right (inside the button: the badges of the row below are clear).
    placeSticker(screen, st, `store_x2${i}`, o && o.multiplier > 1 ? `×${o.multiplier}` : null, X2_COLOR, button.x + 124, button.y + 19, 'left');
    // The gold tab: the exchange's gold coin in place of the diamond.
    const coinKey = `coin${i}`;
    let coin = st.controls[coinKey];
    if (!coin && o && tab === 'gold') {
      coin = coinSprite(fwp);
      if (coin) {
        coin.mouseEnabled = false;
        coin.mouseChildren = false;
        coin.x = button.x + 113.6;
        coin.y = button.y + 4.6;
        screen.addChild(coin);
        st.controls[coinKey] = coin;
      }
    }
    if (coin) coin.visible = o !== null && tab === 'gold';
    if (!o) return;
    if (price) {
      setRawText(price, offerPrice(o, lang));
      prices.push(price);
    }
    if (amount) {
      setRawText(amount, tab === 'gold' ? groupDigits(o.gold) : String(o.diam));
      amounts.push(amount);
    }
  });
  evenFont(prices, PAY_PRICE.size, 10);
  evenFont(amounts, PAY_AMOUNT.size, 11);
  // The header of the block ("Купить алмазы"), the diamonds / gold switch and the first purchase banner.
  const header = st.controls.header ?? (st.controls.header = childNear(screen, 240.55, 111, (c) => /^instance\d+$/.test(String(c.name)) && typeof c.text === 'string'));
  if (header) {
    header.visible = true;
    // The asset's own markup (bold, centred) with the tab's title.
    header.htmlText = `<p align="center"><font face="Arial" size="13" color="#000000"><b>${tab === 'gold' ? MSG_STORE_BUY_GOLD : MSG_STORE_BUY_DIAMONDS}</b></font></p>`;
  }
  const hasGold = offers.some((o) => o.shop && o.shop.tab === 'gold');
  let tabs = st.controls.tabs as Sprite | undefined;
  if (!tabs && hasGold) {
    tabs = new Sprite();
    tabs.name = 'store_tabs';
    const pick = (t: 'diamonds' | 'gold') => () => {
      fwp.store_tab = t;
      interf.render();
    };
    const dia = new TextLink('store_tab_diamonds', MSG_STORE_DIAMONDS, pick('diamonds'));
    const gold = new TextLink('store_tab_gold', MSG_STORE_GOLD, pick('gold'));
    for (const l of [dia, gold]) {
      const f = new TextFormat();
      f.size = 15;
      l.field.setTextFormat(f);
    }
    gold.x = dia.width + 26;
    // Fingers' targets from the header's line (not a control) down to the buttons' cells, the gap between the tabs
    // shared.
    dia.setHitRect({ x: -10, y: -17, width: dia.width + 23, height: 50 });
    gold.setHitRect({ x: -13, y: -17, width: gold.width + 23, height: 50 });
    tabs.addChild(dia);
    tabs.addChild(gold);
    tabs.x = 206;
    tabs.y = 134;
    screen.addChild(tabs);
    st.controls.tabs = tabs;
    st.controls.tabDia = dia;
    st.controls.tabGold = gold;
  }
  if (tabs) {
    tabs.visible = hasGold;
    st.controls.tabDia.alpha = tab === 'diamonds' ? 1 : 0.55;
    st.controls.tabGold.alpha = tab === 'gold' ? 1 : 0.55;
  }
  let banner = st.controls.banner;
  if (!banner) {
    banner = label('', { size: 11, bold: true, color: X2_COLOR, align: 'right', width: 240 });
    banner.name = 'store_first_double';
    banner.x = 354;
    banner.y = 138;
    screen.addChild(banner);
    st.controls.banner = banner;
  }
  banner.text = double ? MSG_STORE_FIRST_DOUBLE : '';
  shrinkToFit(banner, 8);
  // The limited offer (the starter pack): a button under the block, its price on the second line.
  let ob = st.controls.offer as BigButton | undefined;
  if (offer) {
    if (!ob) {
      ob = new BigButton(fwp, 'store_offer', MSG_STORE_STARTER, 'green');
      // Under the six buttons, above the exchange.
      ob.x = 331;
      ob.y = 322;
      ob.addEventListener(MouseEvent.CLICK, () => {
        const cur = storeLayout(fwp, m!.store!.offers() ?? []).offer;
        if (cur) openStoreDialog(interf, cur.productId);
      });
      screen.addChild(ob);
      st.controls.offer = ob;
    }
    ob.setText(`${MSG_STORE_STARTER}\n${offerPrice(offer, lang)}`);
    ob.visible = true;
  } else if (ob) ob.visible = false;
  // (beside the button: its caption fills it)
  placeSticker(screen, st, 'store_offer_once', offer ? translate(MSG_STORE_ONCE_TAG) : null, TAG_COLOR, 331 + 145 + 6, 322 + 11, 'left');
  return true;
}

/** A click on a store control of menu_pay (a buy button): its purchase dialog; true when it was one. */
export function storeClick(interf: any, name: string): boolean {
  const i = (STORE_BUTTONS as readonly string[]).indexOf(name);
  if (i < 0) return false;
  const fwp = interf.FWP;
  const m = portOf(fwp);
  const offers = m && m.store ? m.store.offers() : null;
  if (!offers) return true;
  const o = storeLayout(fwp, offers).slots[i];
  if (o) openStoreDialog(interf, o.productId);
  return true;
}

/**
 * A "donate" button ("+" beside the gold and the diamonds of the main map; "Buy diamonds" of the shop, the darts and
 * Papa John's shop) opens the pay screen: on its gold tab from the "+" beside the gold, else on its diamonds tab.
 */
export function storeTabFor(interf: any, target: any): void {
  const fwp = interf.FWP;
  const cur = interf.cur_screen && interf.cur_screen[interf.cur_screen[0]];
  fwp.store_tab = cur && cur.name === 'mainframe' && target && Number(target.x) < 700 ? 'gold' : 'diamonds';
}

/** The hover text of a store button: { header, text } or null for other names. */
export function storePopup(interf: any, name: string): { header: string; text: string } | null {
  const fwp = interf.FWP;
  const m = portOf(fwp);
  const offers = m && m.store ? m.store.offers() : null;
  if (!offers || offers.length === 0) return null;
  if (name === 'donate') return { header: MSG_STORE_DIAMONDS, text: MSG_STORE_DONATE_HINT };
  const layout = storeLayout(fwp, offers);
  const i = (STORE_BUTTONS as readonly string[]).indexOf(name);
  const o = name === 'store_offer' ? layout.offer : i >= 0 ? layout.slots[i] : null;
  if (!o) return null;
  const lines = [`${MSG_STORE_CONTENTS}: ${contentsOf(o, 1)}`];
  if (o.multiplier > 1) lines.push(MSG_STORE_DOUBLE);
  if (o.kind === 'bundle') lines.push(MSG_STORE_ONCE);
  if (o.label === 'popular') lines.push(MSG_STORE_POPULAR);
  if (o.label === 'best_value') lines.push(MSG_STORE_BEST);
  lines.push(offerPrice(o));
  return { header: o.kind === 'bundle' ? MSG_STORE_STARTER : layout.tab === 'gold' ? MSG_STORE_GOLD : MSG_STORE_DIAMONDS, text: lines.join('\n') };
}

/** What a pack credits in words ("12 алм., 2 000 зол., 2 × стимпакет"), `k` times (the first purchase x2). */
function contentsOf(o: StoreOffer, k: number, tr: (s: string) => string = (s) => s): string {
  const parts: string[] = [];
  if (o.diam) parts.push(`${o.diam * k} ${tr(MSG_UNIT_DIAMONDS)}`);
  if (o.gold) parts.push(`${groupDigits(o.gold * k)} ${tr(MSG_UNIT_GOLD)}`);
  for (const [id, n] of Object.entries(o.items)) parts.push(`${n * k} × ${id === 'stimpack' ? tr(MSG_STIMPACK) : id}`);
  return parts.join(', ');
}

const fill = (template: string, values: Record<string, string>) => template.replace(/\{(\w+)\}/g, (all, k) => values[k] ?? all);

/** The purchase dialog's model of an offer: every text in the player's language, the buttons' labels as source texts. */
export function storeDialogModel(fwp: any, o: StoreOffer, payment: StorePayment): StoreDialogModel {
  const t = translate;
  const gold = o.kind === 'currency_pack' && o.gold > 0 && o.diam === 0;
  const icons: StoreDialogModel['icons'] = [];
  if (o.diam > 0) icons.push({ icon: diamondSprite(fwp), amount: String(o.diam) });
  if (o.gold > 0) icons.push({ icon: coinSprite(fwp), amount: groupDigits(o.gold) });
  const lines: string[] = [];
  if (o.kind === 'bundle') {
    lines.push(`${t(MSG_STORE_CONTENTS)}: ${contentsOf(o, 1, t)}.`);
    lines.push(t(MSG_STORE_ONCE));
  } else if (o.bonusPct > 0) lines.push(fill(t(MSG_STORE_BONUS_LINE), { n: String(o.bonusPct) }));
  else lines.push(t(MSG_STORE_BASE_LINE));
  if (o.multiplier > 1) lines.push(fill(t(MSG_STORE_FIRST_LINE), { k: String(o.multiplier), n: String(o.diam * o.multiplier), m: String(o.diam) }));
  if (payment === 'test') lines.push(t(MSG_STORE_TEST_NOTE));
  return {
    title: t(o.kind === 'bundle' ? MSG_STORE_STARTER : gold ? MSG_STORE_BUY_GOLD : MSG_STORE_BUY_DIAMONDS),
    icons,
    badge: offerBadge(o),
    tag: o.label ? t(o.label === 'popular' ? MSG_STORE_POPULAR_TAG : MSG_STORE_BEST_TAG) : null,
    lines,
    price: offerPrice(o),
    buy: MSG_STORE_BUY,
    cancel: MSG_STORE_CANCEL,
    ok: MSG_STORE_OK,
    wait: MSG_STORE_WAIT,
  };
}

/** What the dialog does with the store's answer: credited or cancelled close it, the rest is said in it. */
export function storeOutcome(r: StoreBuyResult): StoreDialogOutcome {
  if (r.status === 'credited' || r.status === 'cancelled') return { close: true };
  if (r.status === 'unavailable') return { close: false, message: [translate(MSG_STORE_SOON), translate(MSG_STORE_SOON_NOTE)] };
  if (r.status === 'pending') return { close: false, message: [translate(MSG_STORE_PENDING)] };
  return { close: false, message: [translate(MSG_STORE_FAILED)] };
}

/**
 * The purchase dialog of a pack (ui/store-dialog.ts) over the pay screen: its "Buy" runs FWP.net_store_buy (the
 * host's store: Google Play, the development provider, or the skeleton's "payment is not connected yet").
 */
export function openStoreDialog(interf: any, productId: string): void {
  const fwp = interf.FWP;
  const m = portOf(fwp);
  const store = m && m.store;
  if (!store || fwp.store_dialog || fwp.ad_busy || !fwp.display_system) return;
  const o = (store.offers() ?? []).find((x) => x.productId === productId);
  if (!o) return;
  // The hover text of the pack (a desktop) goes with the screen under the dialog.
  if (interf.popup) {
    interf.removeEventListener(Event.ENTER_FRAME, interf.popup_displace);
    interf.popup.visible = false;
  }
  const dialog = new StoreDialog(fwp, storeDialogModel(fwp, o, store.payment()), {
    buy: () => new Promise((resolve) => fwp.net_store_buy(o.productId, (r: StoreBuyResult) => resolve(storeOutcome(r)))),
    onClosed: () => {
      if (fwp.store_dialog === dialog) fwp.store_dialog = null;
    },
  });
  fwp.store_dialog = dialog;
  fwp.display_system.addChild(dialog.root);
  dialog.attach();
}

/**
 * The caption of the "donate" button of the shop, the darts and Papa John's shop (a text field over the button):
 * "Buy diamonds" with the store's offers, "Exchange diamonds" without them (runtime/product-texts.ts; the button opens
 * the pay screen either way).
 */
function renderDonateCaption(fwp: any, screen: any): void {
  const button = screen && screen.getChildByName('donate');
  if (!button) return;
  const want = storeOffered(fwp) ? STORE_CAPTION_HTML : DONATE_CAPTION_HTML;
  const b = button.getBounds(screen);
  for (let i = 0; i < screen.numChildren; i++) {
    const t = screen.getChildAt(i);
    if (!(t instanceof TextField) || (t as any).$caption === want) continue;
    const r = t.getBounds(screen);
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;
    if (cx < b.x || cx > b.x + b.width || cy < b.y || cy > b.y + b.height) continue;
    t.htmlText = want;
    (t as any).$caption = want;
  }
}

// ------------------------------------------------------------------------------------------------ settings

/** Under the worm names of the settings screen, above "save" (the right panel has no room). */

/**
 * The app's controls of the account (the settings screen's account dialog: src/game/ui/account-dialog.ts), none on the
 * web page: the ad consent form where the law asks for it (UMP: privacy options required) and linking the account to
 * Google Play Games (a second way into it on a new device).
 */
export function accountExtras(interf: any): Array<{ name: string; label: string; run(): void }> {
  const fwp = interf && interf.FWP;
  const m = portOf(fwp);
  if (!m) return [];
  const out: Array<{ name: string; label: string; run(): void }> = [];
  if (m.playGames && m.playGames.available() && m.playGames.linked() === false && fwp.user && fwp.user.player_id) {
    out.push({
      name: 'play_games_link',
      label: MSG_PLAY_GAMES_LINK,
      run: () => {
        void m.playGames!.link().then((r) => {
          fwp.net_notice(r === 'linked' ? MSG_PLAY_GAMES_LINKED : MSG_PLAY_GAMES_FAILED, 4000);
          interf.render();
        });
      },
    });
  }
  if (m.ads && m.ads.privacyOptionsRequired()) out.push({ name: 'ad_privacy', label: MSG_AD_PRIVACY, run: () => void m.ads!.showPrivacyOptions() });
  return out;
}

// ------------------------------------------------------------------------------------------------ entry point

/** The screens that lead to the store (their "donate" buttons) and the store itself (pay). */
const STORE_SCREENS = ['mainframe', 'pay', 'shop', 'roul', 'papa_shop'];

/** The monetization controls of a rendered screen (new_interf.render, last; every render, idempotent). */
export function renderMonetization(interf: any, name: string, screen: any): void {
  const fwp = interf.FWP;
  const m = portOf(fwp);
  if (!screen || typeof screen.addChild !== 'function') return;
  // The daily present's own "Accept" with or without a store or ads (its ad button: the skeleton without them).
  if (name === 'present') renderPresent(interf, screen, m);
  if (!m) return;
  const st = stateOf(screen);
  const specs = AD_SPECS.filter((s) => s.screen === name);
  const storeScreen = STORE_SCREENS.indexOf(name) != -1;
  if (!st.refreshed && (specs.length > 0 || storeScreen || name === 'settings')) {
    st.refreshed = true;
    if (specs.length > 0 && m.ads) m.ads.refresh(() => rerender(interf, screen));
    // The pay screen always asks again (the prices, the starter pack's window); the others while no catalogue came.
    if (storeScreen && m.store && (name === 'pay' || m.store.offers() === null)) m.store.refresh(() => rerender(interf, screen));
    if (name === 'settings') {
      if (m.ads) m.ads.refresh(() => rerender(interf, screen));
      if (m.playGames) m.playGames.refresh(() => rerender(interf, screen));
    }
  }
  for (const spec of specs) if (!spec.own) placeAdButton(interf, screen, st, spec, m);
  if (name === 'shop' || name === 'roul' || name === 'papa_shop') renderDonateCaption(fwp, screen);
  // Good moments for Google Play's review prompt (rate-limited by the host).
  if (name === 'end_game_win' && !st.controls.reviewAsked && m.goodMoment) {
    st.controls.reviewAsked = true;
    m.goodMoment(fwp.end_game && fwp.end_game.levels_upg ? 'level_up' : 'victory');
  }
}

